import { useState, useMemo } from 'react';
import { Stack, useTheme } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';

import { Modal } from 'sharedComponents/Modal';
import { districtOption, schoolOption, SCHOOLROLES_FORM_ID } from './data';
import { getVacanciesModalBodySx, VacancyModalTitle } from './styles';
import { VacanciesModalProps, LocationVacancyFormValues } from './types';
import { ATSVacanciesModalTestIds } from 'data-testids/ATS';
import { useQueryGetVacanciesByJobId } from 'features/TabbedJobView/VacanciesTab/queries';
import { VacanciesSkeleton } from './components/VacanciesSkeleton';
import { TabbedJobViewChip } from 'features/TabbedJobView/components/TabbedJobViewChip';
import { useCreateEditVacanciesMutation } from './mutations';
import { VacanciesForm } from 'features/VacanciesModal/components/VacanciesForm';

export const VacanciesModal: React.FC<VacanciesModalProps> = ({
  open,
  onClose,
  roleId,
  onSuccess,
}) => {
  const [currentPane, setCurrentPane] = useState(null);

  const { data, isLoading: isQueryLoading } = useQueryGetVacanciesByJobId(roleId);
  const theme = useTheme();

  const formValues = useMemo(
    () => ({
      district_ftes_total: data?.district_ftes_total ?? 1,
      schoolroles: data?.schoolroles ?? [],
    }),
    [data]
  );

  const initialPane = useMemo(() => {
    if (!data) return null;
    return data.schoolroles?.length > 0 ? schoolOption : districtOption;
  }, [data]);

  const displayPane = currentPane ?? initialPane;

  const methods = useForm({
    defaultValues: {
      district_ftes_total: 1,
      schoolroles: [],
    },
    values: formValues,
  });
  const { handleSubmit, watch } = methods;

  const { mutate } = useCreateEditVacanciesMutation(roleId, {
    onSuccess: () => {
      onClose();
      onSuccess?.();
    },
  });

  const handleFormSubmit = (formData: LocationVacancyFormValues) => {
    if (displayPane?.type === 'school') {
      mutate({
        data: {
          schoolroles: formData.schoolroles,
        },
      });
    } else {
      mutate({
        data: {
          district_ftes_total: formData.district_ftes_total,
        },
      });
    }
  };

  const getVacancyCount = () => {
    if (displayPane?.type === 'school') {
      const schoolRoles = watch(SCHOOLROLES_FORM_ID);
      return schoolRoles?.reduce((acc, role) => acc + role.ftes_total, 0) ?? 0;
    } else {
      return watch('district_ftes_total') ?? 0;
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentPane(null);
  };

  const isLoading = initialPane === null || isQueryLoading;

  return (
    <>
      <Modal
        sx={{ maxWidth: null, minHeight: '60%' }}
        open={open}
        onClose={handleClose}
        title={
          <Stack direction="row">
            <VacancyModalTitle data-testid={ATSVacanciesModalTestIds.MODAL_TITLE} variant="h2">
              Select vacancy locations
            </VacancyModalTitle>
            <TabbedJobViewChip
              sx={{ ml: 1 }}
              dataTestId={ATSVacanciesModalTestIds.VACANCIES_COUNT_CHIP}
              label={getVacancyCount() === 1 ? '1 vacancy' : `${getVacancyCount()} vacancies`}
            />
          </Stack>
        }
        bodySx={getVacanciesModalBodySx(theme)}
        childrenSx={{
          marginTop: initialPane === null ? theme.spacing(1) : undefined,
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}
      >
        {isLoading ? (
          <VacanciesSkeleton />
        ) : (
          <FormProvider {...methods} key={roleId}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <VacanciesForm
                displayPane={displayPane}
                onSetType={setCurrentPane}
                onClose={handleClose}
              />
            </form>
          </FormProvider>
        )}
      </Modal>
    </>
  );
};
